import React from 'react';
import { IPatient } from '../../modules/unsw-fhir/Patient';
import { QuestionnaireItemQuestionComponent } from '../../modules/unsw-fhir/Questionnaire';
import QuestionnaireMutator from '../../modules/unsw-fhir-questionnaire/Utilities/QuestionnaireMutator';
import {
  isQuestionnaireQuestionItem,
  isQuestionnaireOptionCoding,
  isQuestionnaireOptionDate,
  isQuestionnaireOptionString,
  isQuestionnaireOptionTime,
  isQuestionnaireOptionInteger,
} from '../../modules/unsw-fhir/Questionnaire/typeGuards';
import { getQuestionnaireById } from '../../apis/questionnaire';
import { getQuestionnaireResponseById } from '../../apis/questionnaireResponse';
import { ICarePlan } from '../../modules/unsw-fhir/CarePlan';
import { getBirthDate } from '../../selectors/patient';
import { getPatientById } from '../../apis/patient';
import {
  getReferenceId,
  getFirstQuestionnaireReference,
} from '../../modules/unsw-fhir/Reference';
import { QuestionnaireResponseItemComponent } from '../../modules/unsw-fhir/QuestionnaireResponse';
import {
  isQuestionnaireResponseItemAnswer,
  isCodingAnswerComponent,
  isStringAnswerComponent,
  isDateAnswerComponent,
  isDateTimeAnswerComponent,
  isIntegerAnswerComponent,
  isDecimalAnswerComponent,
  isBooleanAnswerComponent,
} from '../../modules/unsw-fhir/QuestionnaireResponse/typeGuards';
import Container from '../../components/Item/Container/Container';
import { Row, Col, Spinner, Alert } from 'reactstrap';
import { useDataApi } from '../../modules/unsw-hooks';
import { getAllCarePlans } from '../../apis/carePlan';
import moment from 'moment';
import { CSVLink } from 'react-csv';

type Header = { label: string; key: string };

const processPatient = (
  patient: IPatient | undefined,
): [Header[], { [index: string]: string }] => {
  const headers = [
    {
      label: 'patientId',
      key: 'patientId',
    },
    {
      label: 'birthDate',
      key: 'birthDate',
    },
    {
      label: 'patientActive',
      key: 'patientActive',
    },
  ];

  if (!patient) return [headers, {}];
  const data = {
    birthDate: getBirthDate(patient),
    patientId: patient.id!,
    patientActive: patient.active ? '1' : '0',
  };
  return [headers, data];
};

const createPrefixLabel = (prefix: string, label: string) =>
  `${prefix}_${label}`;

const processResponseItem = (
  response: QuestionnaireResponseItemComponent,
  prefix: string = '',
): [string, string][] => {
  const createLabel = (label: string) => createPrefixLabel(prefix, label);
  const createChoiceLabel = (label: string) =>
    createPrefixLabel(createLabel(response.linkId), label);
  let answers: [string, string][] = [];
  if (isQuestionnaireResponseItemAnswer(response)) {
    answers = response.answer.map(answer => {
      if (isCodingAnswerComponent(answer)) {
        return [createChoiceLabel(answer.valueCoding.code!), '1'];
      }
      if (isStringAnswerComponent(answer)) {
        return [createLabel(response.linkId), answer.valueString];
      }
      if (isDateAnswerComponent(answer)) {
        return [
          createLabel(response.linkId),
          moment(answer.valueDate).format('DD-MM-YYYY'),
        ];
      }
      if (isDateTimeAnswerComponent(answer)) {
        return [createLabel(response.linkId), answer.valueDateTime];
      }

      if (isIntegerAnswerComponent(answer)) {
        return [createLabel(response.linkId), answer.valueInteger.toString()];
      }
      if (isDecimalAnswerComponent(answer)) {
        return [createLabel(response.linkId), answer.valueDecimal.toString()];
      }
      if (isBooleanAnswerComponent(answer)) {
        return [createLabel(response.linkId), answer ? '1' : '0'];
      }
      throw new Error('Unsupported Answer type');
    });
  }

  return answers;
};

const parseQuestionnaireItem = (
  question: QuestionnaireItemQuestionComponent,
  prefix: string = '',
): Header[] => {
  const createLabel = (label: string) => createPrefixLabel(prefix, label);
  const createChoiceLabel = (label: string) =>
    createPrefixLabel(createLabel(question.linkId), label);
  let choices: string[] = [];

  switch (question.type) {
    case 'choice':
      if (question.option) {
        choices = choices.concat(
          question.option
            .map(option => {
              if (isQuestionnaireOptionCoding(option)) {
                return option.valueCoding.code!;
              }
              throw new Error('Unsupported Option type');
            })
            .map(opt => createChoiceLabel(opt)),
        );
      }

      return choices.map(choice => ({ key: choice, label: choice }));
    case 'open-choice':
      choices = [createChoiceLabel('openAnswer')];
      if (question.option) {
        choices = choices.concat(
          question.option
            .map(option => {
              if (isQuestionnaireOptionCoding(option)) {
                return option.valueCoding.code!;
              }
              if (isQuestionnaireOptionDate(option)) {
                return option.valueDate;
              }
              if (isQuestionnaireOptionTime(option)) {
                return option.valueTime;
              }
              if (isQuestionnaireOptionString(option)) {
                return option.valueString;
              }
              if (isQuestionnaireOptionInteger(option)) {
                return option.valueInteger.toString();
              }
              throw new Error('Unsupported Option type');
            })
            .map(opt => createChoiceLabel(opt)),
        );
      }
      return choices.map(choice => ({ key: choice, label: choice }));
    default:
      const linkId = question.linkId;
      const label = createLabel(linkId);
      return [{ key: label, label }];
  }
};

const processsQuestionnaire = (
  mutator: QuestionnaireMutator,
): [Header[], { [index: string]: string }] => {
  let headers: Header[] = [];
  let data = {};

  const questionnaireItems = mutator.getQuestionnaireItems();

  const questionHeaders = Array.from(questionnaireItems)
    .map(([_, { value }]) => value)
    .filter((value): value is QuestionnaireItemQuestionComponent =>
      isQuestionnaireQuestionItem(value),
    )
    .map(value => parseQuestionnaireItem(value, mutator.questionnaire.name))
    .flat(1);

  return [headers.concat(questionHeaders), data];
};

const processResponseData = (
  mutator: QuestionnaireMutator,
): { [index: string]: string } => {
  const items = Array.from(mutator.getQuestionnaireResponses())
    .map(
      ([_, container]) =>
        processResponseItem(container.value, mutator.questionnaire.name),
      mutator.questionnaire.name,
    )
    .flat()
    .reduce<{ [index: string]: string }>((pv, cv) => {
      pv[cv[0]] = cv[1];
      return pv;
    }, {});

  return items;
};
export const processCsvGenerateRequestByCarePlans = async (
  carePlans: ICarePlan[],
): Promise<[Header[], { [index: string]: string }[]]> => {
  let gHeaders: Header[] = [];
  let gData: { [index: string]: string }[] = [];

  for (const carePlan of carePlans) {
    const [header, data] = await processCsvGenerateRequestByCarePlan(carePlan);
    gHeaders = header;
    gData.push(data);
  }

  return [gHeaders, gData];
};

export const processCsvGenerateRequestByCarePlan = async (
  carePlan: ICarePlan,
): Promise<[Header[], { [index: string]: string }]> => {
  try {
    const gHeaders: Header[] = [];
    let gData: { [index: string]: string } = {};
    const patientId = getReferenceId(carePlan.subject);
    const patient = await getPatientById(patientId!);
    if (!patient) return [[], {}];
    const [patientHeaders, patientData] = processPatient(patient);

    gHeaders.push(...patientHeaders);
    gData = { ...gData, ...patientData };
    if (carePlan.activity)
      for (const activity of carePlan.activity) {
        const questionnaireResponseId = getReferenceId(
          getFirstQuestionnaireReference(activity.outcomeReference),
        );

        const questionnaireId = getReferenceId(activity.detail!.definition);

        const [headers, data] = await processCsvGenerateRequestByQuestionnaire(
          questionnaireId!,
          questionnaireResponseId,
        );

        gHeaders.push(...headers);
        gData = { ...gData, ...data };
      }

    return [gHeaders, gData];
  } catch (e) {
    console.error(e);
    return [[], {}];
  }
};

export const processCsvGenerateRequestByQuestionnaire = async (
  questionnaireId: string,
  questoinnaireReponseId?: string,
): Promise<[Header[], { [index: string]: string }]> => {
  const questionnaire = await getQuestionnaireById(questionnaireId);
  if (!questionnaire) throw new Error('questionnaire not found');
  const mutator = new QuestionnaireMutator(questionnaire);
  const [questionaireHeaders, questionnaireData] =
    processsQuestionnaire(mutator);

  if (!questoinnaireReponseId) return [questionaireHeaders, questionnaireData];
  const questionnaireResponse = await getQuestionnaireResponseById(
    questoinnaireReponseId,
  );
  if (!questionnaireResponse) throw new Error('questionnaire not found');
  mutator.updateResponse(questionnaireResponse);

  const responseData = processResponseData(mutator);
  return [questionaireHeaders, { ...responseData, ...questionnaireData }];
};

const CsvGeneratorLoading = () => (
  <>
    <Row>
      <Col>
        <p>Processsing your CSV Request</p>
      </Col>
    </Row>
    <Row>
      <Col className="text-center">
        <Spinner />
      </Col>
    </Row>
  </>
);

const CsvGeneratorError = () => (
  <Alert color="danger">There's been an error, please try again</Alert>
);
const CsvGeneratorDownload = ({
  headers,
  data,
}: {
  headers: Header[];
  data: { [index: string]: string }[];
}) => (
  <Col className="text-center">
    <CSVLink
      headers={headers}
      data={data}
      filename={`${moment().format('DD-MM-YYY-h-mm')}.csv`}
      className="btn btn-primary text-center"
      target="_blank">
      Download
    </CSVLink>
  </Col>
);

export const CsvGenerator = () => {
  const [
    { data: carePlans, isError: isCarePlanError, isLoading: carePlanLoading },
  ] = useDataApi(getAllCarePlans, []);

  const [{ data: csvPayload, isError: isCsvError, isLoading: csvLoading }] =
    useDataApi(processCsvGenerateRequestByCarePlans, undefined, carePlans);

  const loading = carePlanLoading || csvLoading;
  const complete =
    !loading && carePlans !== undefined && csvPayload !== undefined;
  const isError = isCarePlanError || isCsvError;
  console.log(csvPayload);
  return (
    <Container>
      {isError ? (
        <CsvGeneratorError />
      ) : loading ? (
        <CsvGeneratorLoading />
      ) : complete ? (
        <CsvGeneratorDownload headers={csvPayload![0]} data={csvPayload![1]} />
      ) : null}
    </Container>
  );
};
